import React, {useCallback} from 'react'
import {useIntl} from 'react-intl'
import {Box} from '@chakra-ui/react'
import {IconButton, Icon, Loader} from '_uiComponents'
import {useWishlist, useWishlistActions} from '_customHooks'
import type {ProductType} from '_types'

export interface WishlistButtonProps {
    product: ProductType
    variant?: 'wishlist' | 'shoptheimage' | 'default'
    selectedSize?: string | null
    isMobile: boolean
    isTablet: boolean
    onWishlistItemRemove?: () => void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    styles?: Record<string, any>
}

/**
 * @name WishlistButton
 *
 * @component
 *
 * @description
 * Renders a button for adding or removing products from the wishlist.
 *
 * @param {WishlistButtonProps} props - The properties for the `WishlistButton` component.
 *
 * @returns {React.FC<WishlistButtonProps>} The rendered `WishlistButton` component.
 */
export const WishlistButton: React.FC<WishlistButtonProps> = ({product, variant, selectedSize, isMobile, isTablet, onWishlistItemRemove, styles = {}}) => {
    const {formatMessage} = useIntl()
    const {isProductInWishlist} = useWishlist()
    const {addItemToWishlist, removeItemFromWishlist, productAlreadyInWishlist, isActionLoading} = useWishlistActions()

    const shortDescription = product.c_shortDescription || product.c_variationGroupsData?.[0]?.shortDescription || product.c_variantData?.shortDescription
    const isInWishlist = isProductInWishlist(product.id, selectedSize, variant)
    const size = isMobile || isTablet ? 'iconButtonMd' : 'iconButtonLg'

    const addToWishlistAriaLabel = formatMessage(
        {
            id: 'button.common.add.to.wishlist',
            defaultMessage: 'Save in Wishlist {0}',
        },
        {
            0: shortDescription,
        },
    )
    const removeFromWishlistAriaLabel = formatMessage(
        {
            id: 'button.common.remove.from.wishlist',
            defaultMessage: 'Remove {0} from Wishlist',
        },
        {
            0: shortDescription,
        },
    )

    const buttonAriaLabel = !(variant === 'wishlist') ? addToWishlistAriaLabel : isInWishlist ? removeFromWishlistAriaLabel : addToWishlistAriaLabel

    const handleClick = useCallback(async () => {
        const isInWishlist = isProductInWishlist(product.id, selectedSize, variant)

        if (variant === 'wishlist') {
            if (isInWishlist) {
                await removeItemFromWishlist(product.id)
                onWishlistItemRemove?.()
            }
        } else {
            if (isInWishlist) {
                productAlreadyInWishlist(product)
            } else {
                await addItemToWishlist(product, selectedSize ?? product.id)
            }
        }
    }, [variant, selectedSize, product, isProductInWishlist, addItemToWishlist, removeItemFromWishlist, productAlreadyInWishlist, onWishlistItemRemove])

    return (
        <>
            {isActionLoading && <Loader />}
            <Box {...styles.favouriteButton} onClick={() => void handleClick()} data-testid="wishlist-button">
                <IconButton
                    icon={<Icon name="IconHeart" size="tile-button" isFilled={isInWishlist} />}
                    size={size}
                    isRounded={true}
                    aria-label={buttonAriaLabel}
                    data-testid="add-to-wishlist-button"
                />
            </Box>
        </>
    )
}
